import { defineStore } from 'pinia';
import { TableConstraints, tableLimitProps, tablePageProps, tableStorePrevContextProps } from '../@types/tables';
import { persistConfig } from '../composables/PiniaWrapperFunctions/PiniaPersistConfig';
import { removeItemFromStorage, roundPageLimit } from '../composables/PiniaWrapperFunctions/storeHelperComposable';

export const defaultTableValues: tableLimitProps & tablePageProps =  {
    defaultLimit: 20,
    currentPageLimit: 20,
    currentPage: 1,
    type: 'current',
    sortOrder: 'asc',
    increment: 10
}

let persistStorage = true;

export const useTableContextStore = defineStore('tableContext', {
    state: () => ({
        context: {} as tableStorePrevContextProps
    }),
    getters: {
        getContext: (state) => {
            return () => state.context
        }
    },
    actions: {
        initContext({ id, childPath = null, activeTab }: tableStorePrevContextProps) {
            if (Object.entries(this.context).length === 0) {
                this.context = { id, path: window.location.pathname, childPath, ...(activeTab !== undefined && { activeTab }) }
            }
        },
        setContext(ctx: tableStorePrevContextProps) {
            this.context = ctx
        },
        deleteContext() {
            removeItemFromStorage('storedTableContexts', null, true)
        }
    },
    persist: persistConfig({ key: 'storedTableContexts' })
})

export const useTableLimitStore = defineStore('tables', {
    state: () => ({
        limits: {} as Record<string, tableLimitProps>,
    }),
    getters: {
        getLimit: (state) => {
            return (id: string, type: TableConstraints = TableConstraints.Current) => {
                const limitKey = type === TableConstraints.Default ? 'defaultLimit' : 'currentPageLimit';

                if (state.limits[id]?.[limitKey] !== undefined) {
                    return roundPageLimit(state.limits[id][limitKey]);
                }

                return defaultTableValues[limitKey];
            }
        },
        getSortOrder: (state) => {
            return (id: string) => state.limits[id]?.sortOrder ?? defaultTableValues.sortOrder
        }
    },
    actions: {
        initLimit(tableId: string) {
            if (!this.limits[tableId]) {
                this.limits[tableId] = {
                    defaultLimit: defaultTableValues.defaultLimit,
                    currentPageLimit: defaultTableValues.currentPageLimit,
                    sortOrder: defaultTableValues.sortOrder
                }
            }
        },
        setLimit(id: string, limit: number, type: TableConstraints = TableConstraints.Current) {
            const limitKey = type === TableConstraints.Default ? 'defaultLimit' : 'currentPageLimit';

            if (!this.limits[id]) {
                this.limits[id] = {
                    defaultLimit: defaultTableValues.defaultLimit,
                    currentPageLimit: defaultTableValues.currentPageLimit,
                    [limitKey]: roundPageLimit(limit)
                }

                return
            }

             this.limits[id][limitKey] = roundPageLimit(limit)
        },
        setSort(id: string, order: 'asc' | 'desc') {
            this.limits[id].sortOrder = order
        },
        deleteLimit(id: string) {
            delete this.limits[id]
            removeItemFromStorage('storedTableLimits', id)
        }
    },
    persist: persistConfig({ key: 'storedTableLimits', storage: localStorage })
})

export const useTablePageStore = defineStore('tablePages', {
    state: () => ({
        pages: {} as Record<string, tablePageProps>
    }),
    getters: {
        getPage: (state) => {
            return (id: string, type: TableConstraints = TableConstraints.Current) => {

                if (type === TableConstraints.Default) {
                    return defaultTableValues.currentPage;
                }

                return state.pages[id]?.currentPage ?? defaultTableValues.currentPage;
            }
        },
    },
    actions: {
        initPage(tableId: string) {
            if (!this.pages[tableId]) {
                this.pages[tableId] = {
                    currentPage: defaultTableValues.currentPage
                }
            }
        },
        setPage(id: string, page: number) {
            if (!this.pages[id]) {
                this.pages[id] = { currentPage: page }
                return
            }

            this.pages[id].currentPage = page
        },
        deletePages(id: string) {
            delete this.pages[id]
            removeItemFromStorage('storedTablePages', id, true)
        }
    },
    persist: persistConfig({ key: 'storedTablePages' })
})

export const useTableFilterStore = defineStore('tableFilters', {
    state: () => ({
        persist: true,
        filters: {} as Record<string, object>
    }),
    getters: {
        getFilters: (state) => {
            return (id: string) => state.filters[id]
        },
        getShouldPersistFilters: (state) => {
            return () => state.persist
        }
    },
    actions: {
        initFilters(id: string | null = null) {
            persistStorage = this.persist
            if (id && !this.filters[id] && this.persist) {
                this.filters[id] = {}
                return
            }
            if (!id && this.persist) {
                this.filters = {}
            }
        },
        setShouldPersistFilters(persist: boolean) {
            this.persist = persist
            persistStorage = persist
        },
        setFilters(id: string, filters: object) {
            if (!this.persist) {return}
            this.filters[id] = filters
        },
        deleteFilters(id: string | null = null) {
            if (id) {
                delete this.filters[id]
            }
            else {
                this.filters = {}
            }

            removeItemFromStorage('storedTableFilters', id || null, true)
        }
    },
    persist: persistStorage ? persistConfig({ key: 'storedTableFilters' }) : false
})
